/**
 * OAuth Integration Types
 *
 * Defines types for OAuth provider integrations and scope extensions.
 */

export type OAuthProvider = "google" | "microsoft";

/**
 * Scope types that can be requested via incremental authorization
 */
export type GoogleScopeType = "gmail_base" | "calendar" | "gmail_modify";

/**
 * Configuration for each scope type
 */
export interface ScopeConfig {
  /** OAuth scopes to request */
  scopes: string[];
  /** State type used in OAuth callback to identify the flow (undefined for initial connect) */
  stateType?: string;
  /** Human-readable name for error messages */
  displayName: string;
}

/**
 * Read + label/modify access to the mailbox. Requested by the initial connect flow AND by
 * the incremental "Grant Access" flow, so the two can never drift apart.
 */
const GMAIL_MODIFY_SCOPES = ["https://www.googleapis.com/auth/gmail.modify"] as const;

/**
 * Google OAuth scope configurations
 */
export const GOOGLE_SCOPE_CONFIGS: Record<GoogleScopeType, ScopeConfig> = {
  gmail_base: {
    scopes: [
      "email",
      "profile",
      "https://www.googleapis.com/auth/gmail.readonly",
      "https://www.googleapis.com/auth/gmail.send",
      "https://www.googleapis.com/auth/gmail.compose",
      // Post-Reply Actions (mark as read, Gmail labels) need gmail.modify, so ask for it
      // up front instead of a second consent round. Inboxes connected before this stay
      // without it — for them the per-inbox "Grant Access" incremental flow is the way in.
      ...GMAIL_MODIFY_SCOPES,
    ],
    // No stateType - this is the initial connect flow
    displayName: "Gmail",
  },
  calendar: {
    scopes: [
      "https://www.googleapis.com/auth/calendar.events",
      "https://www.googleapis.com/auth/calendar.events.freebusy",
    ],
    stateType: "calendar_connect",
    displayName: "Google Calendar",
  },
  gmail_modify: {
    scopes: [...GMAIL_MODIFY_SCOPES],
    stateType: "gmail_modify_connect",
    displayName: "Gmail Modify",
  },
};

/**
 * OAuth credentials for a provider
 */
export interface OAuthCredentials {
  clientId: string;
  clientSecret: string;
}

/**
 * Parameters for building incremental OAuth URL
 */
export interface IncrementalOAuthParams {
  provider: OAuthProvider;
  scopeType: GoogleScopeType;
  inboxId: string;
  oauthTokenId: string;
  loginHintEmail?: string;
  returnTo?: string;
  origin: string;
}

/**
 * Result of building OAuth URL
 */
export interface OAuthUrlResult {
  authUrl: string;
  state: string;
}
